import React, { useState, useRef, useEffect } from 'react';
import {
  unescapeCompanies,
  getCompanyData,
  formMessages,
  getFormErrorElement,
} from './utils';
import {
  eventTypes,
  registerCompanyRequest,
  registerStudents,
} from '../../utils';

import SignerForm from './SignerForm';
import CompanyForm from './CompanyForm';
import StudentsForm from '../../StudentsForm';

import { CheckSubscribe } from '../../CheckSubscribe';
import { Promo } from '../../Promo';
import { Button } from '~c/ui/Button';
import { ButtonTabs } from '~c/ui/ButtonTabs';
import { FormMessage } from '~c/FormMessage';
import openErrorMsgForm from '~c/popups/ErrorSender';

import messages from '~h/messages';
import metrikaGoal from '../../../../helpers/metrikaGoal';
import { registerAnalyticEvent } from '~p/EventReg/analytics';

export const CompanyReg = props => {
  const [isSelfReg, setIsSelfReg] = useState(true);
  const [promo, setPromo] = useState('');
  const [subscribe, setSubscribe] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isPhoneCorrect, setPhoneCorrect] = useState(true);
  const [showAlert, setShowAlert] = useState(false);

  const companyFormRef = useRef(null);
  const signerFormRef = useRef(null);
  const studentsFormRef = useRef(null);

  useEffect(() => {
    setErrorMessages([]);
  }, [isSelfReg]);

  const validateForms = () => {
    const isSignerFormValid = signerFormRef.current?.validateForm();
    const isCompanyFormValid = companyFormRef.current?.validateForm();
    const isStudentsFormValid = !isSelfReg
      ? studentsFormRef.current?.validateForm()
      : true;

    const errors = [];
    const formError = [];
    if (!isSignerFormValid) formError.push(formMessages.signerForm);
    if (!isCompanyFormValid) formError.push(formMessages.companyForm);
    if (!isStudentsFormValid) formError.push(formMessages.studentsForm);
    if (formError.length) errors.push(getFormErrorElement(formError));
    setErrorMessages(errors);

    return isSignerFormValid && isCompanyFormValid && isStudentsFormValid;
  };

  const getCompaniesData = async userCompanies => {
    const companyPromises = userCompanies.map(async userCompany => {
      return getCompanyData(userCompany);
    });
    setCompanies(unescapeCompanies(await Promise.all(companyPromises)));
  };

  useEffect(() => {
    getCompaniesData(props.user?.companies || []);
  }, [props.user]);

  const registerCompany = async (companyData, signer) => {
    const { currentLead } = props;
    const response = await registerCompanyRequest({
      ...companyData,
      currentLead,
      signer,
    });
    if (response.status === 'success') {
      return response.data.COMPANY_ID;
    } else if (response.status === 'error') {
      setIsPending(false);
      setErrorMessages([response.errors[0].message]);
      throw new Error(response.errors[0].message);
    } else {
      setIsPending(false);
      setErrorMessages([messages.unknownError]);
      throw new Error(messages.unknownError);
    }
  };

  const onSubmit = async () => {
    if (isPending) return;

    const { user, eventCode, pageCode, currentLead, sendUsersData } = props;
    const options = {
      subscribe,
      promo,
      eventCodes: [eventCode],
      pageCode,
      isSelfReg,
      currentLead,
      isCompanyReg: true,
    };

    if ((!validateForms() && !isPhoneCorrect) || (!validateForms() && isPhoneCorrect)) {
      if (!validateForms()) console.error('forms invalid');
      if (!isPhoneCorrect)  setShowAlert(true);
    } else if (validateForms() && !isPhoneCorrect) {
      setShowAlert(true);
    } else {
      setShowAlert(false);

      const companyData = companyFormRef.current?.getData();
      const signerData = signerFormRef.current?.getData();
      const studentsData = studentsFormRef.current?.getData();

      setErrorMessages([]);

    if (props.eventTypes === 'CONSULT') metrikaGoal('createConsultCompReg');
    if (props.eventTypes === 'EDU') metrikaGoal('createEduCompReg');

    if (subscribe) sendUsersData({data: user, isNewsSubscribe: subscribe});
    try{
      setIsPending(true);
      options.companyId = await registerCompany(companyData, signerData);
      const response = await registerStudents(
        isSelfReg ? [user] : studentsData,
        options,
        signerData,
        props.eventTypes,
      );
      setIsPending(false);

      switch (response[0].status) {
        case 'success':
          if (props.eventTypes === eventTypes.EDU) {
            if (isSelfReg) {
              props.cbDataForEduServices({
                visualType: 'user',
                body: [
                  {
                    id: window.user.CONTACT_ID,
                    name: window.user.FIRST_NAME,
                    lastName: window.user.LAST_NAME,
                  },
                ],
              });
            } else {
              props.cbDataForEduServices({
                visualType: 'students',
                body: response.map(({ data }, index) => {
                  return {
                    id: data.NEW_CONTACT_ID,
                    name: studentsData[index].name,
                    lastName: studentsData[index].lastName,
                  };
                }),
              });
            }
          }
          metrikaGoal('successReg');
          registerAnalyticEvent();
            props.nextStep();
            break;
          case 'error':
            setErrorMessages([response[0].errors[0].message]);
            break;
          default:
            setErrorMessages([messages.unknownError]);
            throw new Error(response);
        }
      }catch(e){
        const errorText = `${e.name} : ${e.message}`;
        const title = document.getElementsByTagName('h1')[0].innerHTML;
        const currentLocation = window.location.pathname;
        const error = {
          errors: errorText,
          page: `Регистрация на курс (Юр Лицо). Курс: ${title}. URL: ${currentLocation}`
        };
      }
    };
  };

  const removeSelection = () => {
    if (!isPhoneCorrect && showAlert) {
      setPhoneCorrect(!isPhoneCorrect);
      setShowAlert(false);
    }
  };

  return (
    <div
      className={`registration__block ${!props.isCompanyReg ? 'hidden' : ''}`}
    >
      <CompanyForm
        ref={companyFormRef}
        key={companies}
        user={props.user}
        currentCompany={companies[companies.length - 1]}
      />
      <h4 className="heading heading--2">Данные подписанта для договора</h4>
      <SignerForm ref={signerFormRef} />
      <div className="separate" />
      <ButtonTabs
        className="registration__tabs"
        options={[
          {
            label: 'Регистрируюсь сам',
            value: true,
            checked: isSelfReg,
          },
          {
            label: 'Регистрирую другого участника',
            value: false,
            checked: !isSelfReg,
          },
        ]}
        onChange={setIsSelfReg}
      />
      {!isSelfReg && (
        <React.Fragment>
          <div className="separate" />
          <StudentsForm
            ref={studentsFormRef}
            user={props.user}
            companyRegistration
            eventTypes={props.eventTypes}
            showAlert={showAlert}
            setPhoneCorrect={setPhoneCorrect}
            removeSelection={removeSelection}
            isPhoneCorrect={isPhoneCorrect}
          />
        </React.Fragment>
      )}

      <Promo onChange={val => setPromo(val)} eventCode={props.eventCode} />
      <div className="separate" />
      {errorMessages.length >= 1 && !showAlert && (
        <React.Fragment>
          <FormMessage extraClass="error">
            {errorMessages.map(error => (
              <p className="form-message__p" key={error}>
                {error}
              </p>
            ))}
          </FormMessage>
        </React.Fragment>
      )}
      {props.checkSubscribe || props.user?.subscribe || (
        <CheckSubscribe checked={subscribe} onChange={setSubscribe} />
      )}
      <div className="registration__btns">
        <Button isPending={isPending} onClick={onSubmit}>
          Зарегистрироваться
        </Button>
      </div>
    </div>
  );
};
